/**
 * micStream — mic acquisition with graceful text-only fallback.
 *
 * When a real microphone device is available and the user has granted
 * permission, we return the real MediaStream from getUserMedia so the
 * WebRTC transport can send the Founder's audio to the model.
 *
 * When the device is missing (fake-mic-free headless environments, a
 * laptop with the mic disabled at the OS level, or a browser that has
 * revoked permission), getUserMedia rejects with NotFoundError /
 * NotAllowedError / etc. In that case we synthesize a silent
 * MediaStream from an AudioContext so the WebRTC session can still be
 * established. The result:
 *
 *   - session.connect() succeeds because it has a valid audio track.
 *   - No mic barge-in ever fires (the input is genuinely silent).
 *   - RealtimeSession.sendMessage(text) works — the data channel is
 *     independent of the audio track.
 *   - The Founder can type messages and receive spoken replies from
 *     Qoryn without any microphone at all.
 *
 * Founder's rule (2026-08-18): "If text mode fails solely because
 * microphone permission is absent, fix this regression before Phase 1
 * closure. Do not redesign the Founder Console."
 */

/**
 * @typedef {object} MicAcquisitionResult
 * @property {MediaStream} mediaStream - Stream to pass to the WebRTC
 *   transport. Real mic or synthetic-silent.
 * @property {"live"|"text_only"} mode - "live" when the real mic was
 *   acquired; "text_only" when we fell back to a silent stream.
 * @property {string|null} fallbackReason - Human-readable reason we
 *   fell back to silent, or null when the real mic was used.
 * @property {() => void} cleanup - Call on session teardown to release
 *   the underlying AudioContext (silent-mode) or stop all mic tracks
 *   (live-mode).
 */

/**
 * Try to acquire the Founder's microphone; fall back to a synthetic
 * silent MediaStream when the device or permission is unavailable.
 *
 * @returns {Promise<MicAcquisitionResult>}
 */
export async function acquireMicOrSilentFallback() {
    // Some browsers / test harnesses lack navigator.mediaDevices
    // entirely. Fall straight to silent mode in that case.
    if (
        typeof navigator === "undefined" ||
        !navigator.mediaDevices ||
        typeof navigator.mediaDevices.getUserMedia !== "function"
    ) {
        return buildSilentStream({
            reason: "mediaDevices_unavailable",
        });
    }
    try {
        const mediaStream = await navigator.mediaDevices.getUserMedia({
            audio: true,
        });
        return {
            mediaStream,
            mode: "live",
            fallbackReason: null,
            cleanup: () => {
                try {
                    for (const track of mediaStream.getTracks()) {
                        try {
                            track.stop();
                        } catch { /* noop */ }
                    }
                } catch { /* noop */ }
            },
        };
    } catch (err) {
        // NotFoundError: no mic device present.
        // NotAllowedError: user or policy denied permission.
        // OverconstrainedError / NotReadableError / AbortError: hardware busy.
        const reason = err && err.name ? err.name : "unknown_media_error";
        return buildSilentStream({ reason });
    }
}

/**
 * Build a MediaStream that carries a single silent audio track.
 * Sample-accurate silence (a stopped OscillatorNode routed into a
 * MediaStreamAudioDestinationNode) — zero audible signal, zero VAD
 * activation, zero unintended barge-in.
 */
function buildSilentStream({ reason }) {
    // AudioContext is broadly supported (Chrome / Firefox / Safari /
    // Edge). In the rare case it's missing, return an empty MediaStream
    // — the WebRTC transport will still connect, but text-only mode is
    // the fallback of the fallback.
    // eslint-disable-next-line no-undef
    const Ctx = (typeof window !== "undefined" &&
        (window.AudioContext || window.webkitAudioContext)) || null;
    if (!Ctx) {
        try {
            // eslint-disable-next-line no-undef
            return {
                mediaStream: new MediaStream(),
                mode: "text_only",
                fallbackReason: `${reason}_no_audiocontext`,
                cleanup: () => {},
            };
        } catch {
            // Even MediaStream is missing — nothing we can do but
            // report and let the caller fail cleanly.
            const err = new Error(
                `Cannot construct silent audio track: no AudioContext and no MediaStream (reason=${reason}).`,
            );
            err.name = "SilentFallbackUnavailable";
            throw err;
        }
    }
    const ctx = new Ctx();
    const oscillator = ctx.createOscillator();
    const dest = ctx.createMediaStreamDestination();
    // Immediately stop the oscillator — the destination node still
    // emits a silent PCM stream at the AudioContext's sample rate.
    oscillator.connect(dest);
    oscillator.start();
    oscillator.stop(ctx.currentTime);
    return {
        mediaStream: dest.stream,
        mode: "text_only",
        fallbackReason: reason,
        cleanup: () => {
            try {
                for (const track of dest.stream.getTracks()) {
                    try {
                        track.stop();
                    } catch { /* noop */ }
                }
            } catch { /* noop */ }
            try {
                ctx.close();
            } catch { /* noop */ }
        },
    };
}

export default acquireMicOrSilentFallback;
